import { useState, useEffect, useRef } from 'react';
import type { PretextLayoutResult } from '../../lib/pretext/types';
import { buildFontString } from '../../lib/pretext/fonts';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ResultsEditorialProps {
  photoUrl: string | null;
  score: number;
  verdict: string;
  confidenceLabel: string;
  strengths: string[];
  topImprovement: { issue: string; suggestion: string } | null;
  vertical: 'dating' | 'linkedin';
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function scoreColorClass(score: number): string {
  if (score >= 8) return 'score-excellent';
  if (score >= 6) return 'score-good';
  if (score >= 4) return 'score-average';
  return 'score-poor';
}

/* Icons rendered inline — keeps the component self-contained. */
function CheckIcon() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 shrink-0 text-success"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.5 8.5L6.5 11.5L12.5 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 shrink-0 text-warning"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 12.5h4M6.5 14h3M8 2a4.5 4.5 0 0 0-2.5 8.2c.3.2.5.6.5 1v.3h4v-.3c0-.4.2-.8.5-1A4.5 4.5 0 0 0 8 2z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Pretext line-by-line renderer                                      */
/* ------------------------------------------------------------------ */

interface PretextLinesProps {
  text: string;
  lines: PretextLayoutResult['lines'];
  className?: string;
}

/** Render pretext-measured lines or fall back to a plain paragraph. */
function PretextLines({ text, lines, className = '' }: PretextLinesProps) {
  if (lines && lines.length > 0) {
    return (
      <div className={className}>
        {lines.map((line, i) => (
          <span key={i} className="block">
            {line.text}
          </span>
        ))}
      </div>
    );
  }
  return <p className={className}>{text}</p>;
}

/* ------------------------------------------------------------------ */
/*  Score overlay on the photo                                         */
/* ------------------------------------------------------------------ */

interface ScoreOverlayProps {
  score: number;
  verdict: string;
}

function ScoreOverlay({ score, verdict }: ScoreOverlayProps) {
  return (
    <div className="absolute bottom-0 left-0 right-0 rounded-b-xl bg-gradient-to-t from-black/60 to-transparent px-5 pb-4 pt-10">
      <span
        className={`font-display text-4xl ${scoreColorClass(score)}`}
        style={{ textShadow: '0 1px 6px rgba(0,0,0,0.3)' }}
      >
        {score.toFixed(1)}
      </span>
      <span className="ml-2 font-display text-xl text-white/90">{verdict}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function ResultsEditorial({
  photoUrl,
  score,
  verdict,
  confidenceLabel,
  strengths,
  topImprovement,
  vertical,
  className = '',
}: ResultsEditorialProps) {
  const coachingRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<PretextLayoutResult | null>(null);

  /* Build the full coaching body as a single string for pretext. */
  const coachingText = buildCoachingText(
    verdict,
    confidenceLabel,
    strengths,
    topImprovement,
  );

  /* Lazy-load pretext and measure the coaching text. */
  useEffect(() => {
    if (!coachingRef.current) return;
    let cancelled = false;

    const width = coachingRef.current.offsetWidth;
    const font = buildFontString(400, 16, 'ui');
    const lineHeightPx = 16 * 1.6; // matches leading-relaxed at 16px

    import('../../lib/pretext/measure')
      .then(({ measureTextWithLines }) =>
        measureTextWithLines(coachingText, font, width, lineHeightPx),
      )
      .then((result) => {
        if (!cancelled && result) setLayout(result);
      })
      .catch(() => {
        // Pretext unavailable — component remains in fallback mode.
      });

    return () => {
      cancelled = true;
    };
  }, [coachingText]);

  /* Accent colours per vertical for subtle theming. */
  const verticalAccent =
    vertical === 'dating' ? 'border-accent/20' : 'border-accent-secondary-soft';

  return (
    <section
      className={`results-editorial ${className}`}
      data-vertical={vertical}
    >
      {/* ---- Desktop: side-by-side magazine layout ---- */}
      <div className="hidden md:flex md:gap-8 lg:gap-12">
        {/* Photo column */}
        <div
          className="relative shrink-0"
          style={{ maxWidth: 'var(--results-photo-max, 460px)' }}
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt="Your photo"
              className={`w-full rounded-xl shadow-card border ${verticalAccent} object-cover`}
            />
          ) : (
            <div
              className="flex aspect-[3/4] w-full items-center justify-center rounded-xl bg-surface-muted"
              role="img"
              aria-label="Photo placeholder"
            >
              <span className="text-ink-faint text-sm">No photo</span>
            </div>
          )}
          <ScoreOverlay score={score} verdict={verdict} />
        </div>

        {/* Coaching column */}
        <div ref={coachingRef} className="min-w-0 flex-1">
          <CoachingContent
            verdict={verdict}
            confidenceLabel={confidenceLabel}
            strengths={strengths}
            topImprovement={topImprovement}
            layout={layout}
            coachingText={coachingText}
          />
        </div>
      </div>

      {/* ---- Mobile: stacked layout ---- */}
      <div className="md:hidden">
        {/* Photo */}
        <div className="relative mb-5">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt="Your photo"
              className={`w-full rounded-xl shadow-card border ${verticalAccent} object-cover`}
            />
          ) : (
            <div
              className="flex aspect-[3/4] w-full items-center justify-center rounded-xl bg-surface-muted"
              role="img"
              aria-label="Photo placeholder"
            >
              <span className="text-ink-faint text-sm">No photo</span>
            </div>
          )}
          <ScoreOverlay score={score} verdict={verdict} />
        </div>

        {/* Coaching (standard vertical flow) */}
        <CoachingContent
          verdict={verdict}
          confidenceLabel={confidenceLabel}
          strengths={strengths}
          topImprovement={topImprovement}
          layout={null}
          coachingText={coachingText}
        />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Coaching content sub-component                                     */
/* ------------------------------------------------------------------ */

interface CoachingContentProps {
  verdict: string;
  confidenceLabel: string;
  strengths: string[];
  topImprovement: ResultsEditorialProps['topImprovement'];
  layout: PretextLayoutResult | null;
  coachingText: string;
}

function CoachingContent({
  verdict,
  confidenceLabel,
  strengths,
  topImprovement,
  layout,
  coachingText,
}: CoachingContentProps) {
  return (
    <div className="space-y-6">
      {/* Section 1: Verdict + confidence */}
      <div>
        <h3 className="font-display text-xl text-ink mb-1">{verdict}</h3>
        <p className="font-ui text-sm text-ink-muted">{confidenceLabel}</p>
      </div>

      {/* Section 2: Strengths */}
      {strengths.length > 0 && (
        <div>
          <h4 className="font-ui text-sm font-medium text-ink mb-3">
            Strengths
          </h4>
          <ul className="space-y-2" role="list">
            {strengths.map((s, i) => (
              <li
                key={i}
                className="flex items-start gap-2 font-ui text-base text-ink-muted leading-relaxed"
              >
                <CheckIcon />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Section 3: Top improvement */}
      {topImprovement && (
        <div className="rounded-lg border border-border-soft bg-surface/60 p-4">
          <div className="flex items-start gap-2">
            <LightbulbIcon />
            <div>
              <p className="font-ui text-sm font-medium text-ink">
                {topImprovement.issue}
              </p>
              <p className="mt-1 font-ui text-sm text-ink-muted leading-relaxed">
                {topImprovement.suggestion}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pretext-enhanced full coaching block (visible when pretext resolves) */}
      {layout?.lines && (
        <div
          className="font-ui text-base text-ink-muted leading-relaxed"
          data-pretext-lines={layout.lineCount}
        >
          <PretextLines
            text={coachingText}
            lines={layout.lines}
          />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Text builder                                                       */
/* ------------------------------------------------------------------ */

function buildCoachingText(
  verdict: string,
  confidenceLabel: string,
  strengths: string[],
  topImprovement: ResultsEditorialProps['topImprovement'],
): string {
  const parts: string[] = [];
  parts.push(`${verdict}. ${confidenceLabel}.`);
  if (strengths.length > 0) {
    parts.push(`Strengths: ${strengths.join(', ')}.`);
  }
  if (topImprovement) {
    parts.push(`${topImprovement.issue}: ${topImprovement.suggestion}`);
  }
  return parts.join(' ');
}
